import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import MuiAppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../hooks/useAuth';
import ShopSelector from './ShopSelector';

function ResponsiveAppBar({ drawerWidth, handleDrawerOpen, open, shops }) {
  const { user, setCurrentShop } = useAuth();
  const currentShop = user ? user.currentShop : null;
  const isSuperAdmin = user ? user.isSuperAdmin || false : false;
  const isAdmin = user ? user.isAdmin || false : false;
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const navigate = useNavigate();
  const title = 'ANTIKVARIÁT';

  const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
  })(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }));

  const Button = styled(MuiButton)(({ theme, pill }) => ({
    borderRadius: pill ? theme.shape.pillRadius : theme.shape.borderRadius,
  }));

  const settings = [
    {
      name: 'Můj účet',
      path: '/user/account',
      icon: <AccountCircleIcon fontSize="small" />,
    },
    {
      name: 'Odhlásit',
      path: '/logout',
      icon: <LogoutIcon fontSize="small" />,
    },
  ];

  const pages = currentShop
    ? [
        {
          name: 'Seznam knih',
          path: '/books',
          icon: <AutoStoriesIcon />,
        },
        {
          name: 'Nová kniha',
          path: '/books/create',
          icon: <LibraryAddIcon />,
        },
        {
          name: 'Hledání knih',
          path: '/books/search',
          icon: <SearchIcon />,
          // color: 'green',
        },
      ]
    : [];

  const isPageDisplayable = (page) => {
    if (page.isSuperAdmin && !isSuperAdmin) {
      return false;
    }

    if (page.isAdmin && !isAdmin && !isSuperAdmin) {
      return false;
    }

    return true;
  };

  const displayablePages = pages.filter(isPageDisplayable);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigate = (path) => {
    handleCloseNavMenu();

    navigate(path);
  };

  const handleDrawerOpenCallback = () => {
    handleDrawerOpen();
  };

  const UserNameMenuItem = () => {
    if (!user.name || user.name === '') {
      return null;
    }

    return (
      <MenuItem key="account-name">
        <Typography textAlign="center" sx={{ fontWeight: 'bold' }}>
          {user.name}
        </Typography>
      </MenuItem>
    );
  };

  const handleSetShop = (shopUuid) => {
    setCurrentShop(shops.find((shop) => shop.uuid === shopUuid));

    navigate('/');
  };

  if (!user) {
    return (
      <AppBar enableColorOnDark={true}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              variant="h5"
              noWrap
              component="a"
              href="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              {title}
            </Typography>
          </Toolbar>
        </Container>
      </AppBar>
    );
  }

  return (
    <AppBar
      position="fixed"
      color={currentShop?.is_sandbox ? 'error' : 'primary'}
      enableColorOnDark={true}
      open={open}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpenCallback}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="a"
            href="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="main navigation menu"
              aria-controls="navmenu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="navmenu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {displayablePages.map((page) => (
                <MenuItem key={page.name} onClick={handleCloseNavMenu}>
                  <Typography textAlign="center">
                    <NavLink to={page.path}>{page.name}</NavLink>
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Typography
            variant="h5"
            noWrap
            component="a"
            href=""
            sx={{
              mr: 2,
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.3rem',
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            {title}
          </Typography>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {displayablePages.map((page) => (
              <Button
                key={page.name}
                variant="contained"
                startIcon={page.icon}
                onClick={handleNavigate.bind(this, page.path)}
                sx={{
                  my: 2,
                  ml: 0.5,
                  color: 'white',
                  backgroundColor: page.color ? page.color : 'transparent',
                  display: 'block',
                }}
              >
                {page.name}
              </Button>
            ))}
          </Box>
          {currentShop?.is_sandbox && (
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              <Typography variant="h6" noWrap sx={{ color: 'white', fontWeight: 'bold' }}>
                SANDBOX
              </Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            <ShopSelector shops={shops} currentShop={currentShop} onShopSelect={handleSetShop} />
          </Box>
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton
                onClick={handleOpenUserMenu}
                sx={{ p: 0 }}
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
              >
                <Avatar alt="" src="/static/images/avatar/2.jpg" />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <UserNameMenuItem />
              {settings.map((setting) => (
                <MenuItem key={setting.name} onClick={handleCloseUserMenu}>
                  <ListItemIcon>{setting.icon}</ListItemIcon>
                  <Typography textAlign="center">
                    <NavLink to={setting.path}>{setting.name}</NavLink>
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default ResponsiveAppBar;
